import { RotateCcw, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import ErrorDisplay from '../../../components/ErrorDisplay';
import type { ObsessLessUser } from '../../../types/commonTypes';
import { formatDateTime } from '../../../utils/timeUtils';
import { userProfileService } from '../services/userProfileService';

interface UsageTabProps {
    currentUser: ObsessLessUser;
    onUserUpdate: () => Promise<void>;
}

/** The weekly dictation (STT) and voice call counts, which the weekly limits use. */
export const UsageTab = ({ currentUser, onUserUpdate }: UsageTabProps) => {
    const [sttSessionsThisWeek, setSttSessionsThisWeek] = useState<number>(0);
    const [isSavingStt, setIsSavingStt] = useState(false);
    const [sttError, setSttError] = useState<string | null>(null);
    const [sttSuccessMessage, setSttSuccessMessage] = useState<string | null>(null);

    const [voiceConversationsThisWeek, setVoiceConversationsThisWeek] = useState<number>(0);
    const [voiceMinutesThisWeek, setVoiceMinutesThisWeek] = useState<number>(0);
    const [isSavingVoice, setIsSavingVoice] = useState(false);
    const [voiceError, setVoiceError] = useState<string | null>(null);
    const [voiceSuccessMessage, setVoiceSuccessMessage] = useState<string | null>(null);

    useEffect(() => {
        setSttSessionsThisWeek(currentUser.sttUsage?.sessionsThisWeek ?? 0);
        setVoiceConversationsThisWeek(currentUser.voiceUsage?.conversationsThisWeek ?? 0);
        setVoiceMinutesThisWeek(currentUser.voiceUsage?.minutesThisWeek ?? 0);

        setSttError(null);
        setSttSuccessMessage(null);
        setVoiceError(null);
        setVoiceSuccessMessage(null);
    }, [currentUser]);

    const handleSaveSttUsage = async () => {
        setIsSavingStt(true);
        setSttError(null);
        setSttSuccessMessage(null);

        try {
            await userProfileService.updateSttUsage(currentUser.uid, sttSessionsThisWeek);
            setSttSuccessMessage('STT usage updated successfully!');
            await onUserUpdate();
        } catch (err) {
            setSttError(err instanceof Error ? err.message : 'Failed to update STT usage');
        } finally {
            setIsSavingStt(false);
        }
    };

    const handleResetSttSessions = async () => {
        setIsSavingStt(true);
        setSttError(null);
        setSttSuccessMessage(null);

        try {
            await userProfileService.updateSttUsage(currentUser.uid, 0);
            setSttSessionsThisWeek(0);
            setSttSuccessMessage('STT sessions reset.');
            await onUserUpdate();
        } catch (err) {
            setSttError(err instanceof Error ? err.message : 'Failed to reset STT sessions');
        } finally {
            setIsSavingStt(false);
        }
    };

    /** Resets one count and keeps the stored value of the other, not its unsaved edit. */
    const handleResetVoiceUsage = async (field: 'conversations' | 'minutes') => {
        const conversations = field === 'conversations' ? 0 : currentUser.voiceUsage?.conversationsThisWeek ?? 0;
        const minutes = field === 'minutes' ? 0 : currentUser.voiceUsage?.minutesThisWeek ?? 0;

        setIsSavingVoice(true);
        setVoiceError(null);
        setVoiceSuccessMessage(null);

        try {
            await userProfileService.updateVoiceUsage(currentUser.uid, conversations, minutes);
            if (field === 'conversations') {
                setVoiceConversationsThisWeek(0);
            } else {
                setVoiceMinutesThisWeek(0);
            }
            setVoiceSuccessMessage(`Voice ${field} reset.`);
            await onUserUpdate();
        } catch (err) {
            setVoiceError(err instanceof Error ? err.message : `Failed to reset voice ${field}`);
        } finally {
            setIsSavingVoice(false);
        }
    };

    const handleSaveVoiceUsage = async () => {
        setIsSavingVoice(true);
        setVoiceError(null);
        setVoiceSuccessMessage(null);

        try {
            await userProfileService.updateVoiceUsage(
                currentUser.uid,
                voiceConversationsThisWeek,
                voiceMinutesThisWeek
            );
            setVoiceSuccessMessage('Voice usage updated successfully!');
            await onUserUpdate();
        } catch (err) {
            setVoiceError(err instanceof Error ? err.message : 'Failed to update voice usage');
        } finally {
            setIsSavingVoice(false);
        }
    };

    if (!currentUser.sttUsage && !currentUser.voiceUsage) {
        return (
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                <p className="text-sm text-gray-600">This user has not used dictation or voice calls yet.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {/* STT Usage Section */}
            {currentUser.sttUsage && (
                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">STT Usage</h2>

                    {sttError && <ErrorDisplay message={sttError} />}
                    {sttSuccessMessage && (
                        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                            {sttSuccessMessage}
                        </div>
                    )}

                    <div className="text-sm text-gray-800 space-y-4">
                        <div>
                            <label htmlFor="stt-sessions" className="block text-sm font-medium text-gray-700 mb-2">
                                Sessions This Week
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    id="stt-sessions"
                                    type="number"
                                    min={0}
                                    value={sttSessionsThisWeek}
                                    onChange={(e) => setSttSessionsThisWeek(Number(e.target.value))}
                                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                                <Button
                                    icon={<RotateCcw className="w-4 h-4" />}
                                    onClick={handleResetSttSessions}
                                    disabled={isSavingStt}
                                    variant="secondary"
                                >
                                    Reset
                                </Button>
                            </div>
                        </div>

                        <p>
                            Weekly Reset Timestamp: <span className="font-bold">{formatDateTime(currentUser.sttUsage.weeklyResetTimestamp)}</span>
                        </p>

                        <Button
                            icon={<Save className="w-4 h-4" />}
                            onClick={handleSaveSttUsage}
                            disabled={isSavingStt}
                            variant="primary"
                        >
                            {isSavingStt ? 'Saving...' : 'Save STT Usage'}
                        </Button>
                    </div>
                </div>
            )}

            {/* Voice Usage Section */}
            {currentUser.voiceUsage && (
                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Voice Usage</h2>

                    {voiceError && <ErrorDisplay message={voiceError} />}
                    {voiceSuccessMessage && (
                        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                            {voiceSuccessMessage}
                        </div>
                    )}

                    <div className="text-sm text-gray-800 space-y-4">
                        <div>
                            <label htmlFor="voice-conversations" className="block text-sm font-medium text-gray-700 mb-2">
                                Conversations This Week
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    id="voice-conversations"
                                    type="number"
                                    min={0}
                                    value={voiceConversationsThisWeek}
                                    onChange={(e) => setVoiceConversationsThisWeek(Number(e.target.value))}
                                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                                <Button
                                    icon={<RotateCcw className="w-4 h-4" />}
                                    onClick={() => handleResetVoiceUsage('conversations')}
                                    disabled={isSavingVoice}
                                    variant="secondary"
                                >
                                    Reset
                                </Button>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="voice-minutes" className="block text-sm font-medium text-gray-700 mb-2">
                                Minutes This Week
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    id="voice-minutes"
                                    type="number"
                                    min={0}
                                    step="0.1"
                                    value={voiceMinutesThisWeek}
                                    onChange={(e) => setVoiceMinutesThisWeek(Number(e.target.value))}
                                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                                <Button
                                    icon={<RotateCcw className="w-4 h-4" />}
                                    onClick={() => handleResetVoiceUsage('minutes')}
                                    disabled={isSavingVoice}
                                    variant="secondary"
                                >
                                    Reset
                                </Button>
                            </div>
                        </div>

                        <p>
                            Weekly Reset Timestamp: <span className="font-bold">{formatDateTime(currentUser.voiceUsage.weeklyResetTimestamp)}</span>
                        </p>

                        <Button
                            icon={<Save className="w-4 h-4" />}
                            onClick={handleSaveVoiceUsage}
                            disabled={isSavingVoice}
                            variant="primary"
                        >
                            {isSavingVoice ? 'Saving...' : 'Save Voice Usage'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
